import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { FlightsManagerMobile } from '../FlightsManager';
import { DynpackManagerMobile } from '../DynpackManager';
import { HotelManagerMobile } from '../HotelManager';
import { SearchManager } from './SearchManager';
import { EntryPoint, EntryPointProp, EntryPointPropDefault, TripType } from '../../models';
import { isLoggedIn } from '../../redux/userAuthentication/actions';
import { userAuthenticationSelector } from '../../redux/userAuthentication/selectors';
import { getTripType } from '../../redux/currentSearch/selectors';
import { setTripType } from '../../redux/currentSearch/actions';
import { propagateLoginStatusToOdigeoJS } from '../../utils/OdigeoInteractions';
import { PixelTracking } from '../../components/PixelTracking';
import { Config } from '../../utils';
import { setActiveProduct } from '../../redux/activeProduct/actions';

const sendSearcherRenderedAction = (renderTime) => {
    try {
        window.DD_RUM.addAction('flight_searcher_rendered', {
            partition: 'A',
            render_time_ms: renderTime,
        });
    } catch (error) {
        console.warn('[LPS-6229] Tracking error:', error);
    }
};

export const SearchManagerMobile = ({ isHotelOptionEnabled, entryPoint, rootDataAttr }) => {
    const dispatch = useDispatch();
    const {
        usersResponse,
        initial: initialUsersResponse,
        isFetching: isFetchingUsersResponse,
    } = useSelector((state) => userAuthenticationSelector(state));
    const tripType = useSelector(getTripType);
    const isWMP = Config.get('isWMP');
    const isUserLoggedIn = Object.keys(usersResponse).length !== 0 && usersResponse?.userDTO !== null;

    useEffect(() => {
        !initialUsersResponse && !isFetchingUsersResponse && propagateLoginStatusToOdigeoJS(isUserLoggedIn, usersResponse);
    }, [initialUsersResponse, isFetchingUsersResponse, isUserLoggedIn, usersResponse]);
    useEffect(() => {
        dispatch(isLoggedIn());
    }, [dispatch]);
    useEffect(() => {
        if (isWMP && entryPoint === EntryPoint.TRAINS) {
            dispatch(setActiveProduct(entryPoint));
        }
    }, [dispatch, isWMP, entryPoint]);
    useEffect(() => {
        if (entryPoint === EntryPoint.TRAINS && tripType === TripType.MULTIPLE) {
            dispatch(setTripType(TripType.ROUND_TRIP));
        }
    }, [dispatch, entryPoint, tripType]);

    useEffect(() => {
        const renderTime = Math.round(performance.now());

        if (window.DD_RUM) {
            sendSearcherRenderedAction(renderTime);
            return;
        }

        let attempts = 0;
        const interval = setInterval(() => {
            try {
                if (window.DD_RUM) {
                    clearInterval(interval);
                    sendSearcherRenderedAction(renderTime);
                } else if (++attempts >= 10) {
                    clearInterval(interval);
                }
            } catch (error) {
                clearInterval(interval);
                console.warn('[LPS-6229] Tracking error:', error);
            }
        }, 2000);

        return () => clearInterval(interval);
    }, []);

    switch (entryPoint) {
        case EntryPoint.DP:
            return (
                <SearchManager>
                    <PixelTracking entryPoint={entryPoint} />
                    <DynpackManagerMobile rootDataAttr={rootDataAttr} />
                </SearchManager>
            );
        case EntryPoint.HOTELS:
            return (
                <SearchManager>
                    <PixelTracking entryPoint={entryPoint} />
                    <HotelManagerMobile rootDataAttr={rootDataAttr} />
                </SearchManager>
            );
        case EntryPoint.TRAINS:
            return (
                <SearchManager>
                    <FlightsManagerMobile isHotelOptionEnabled={false} rootDataAttr={rootDataAttr} />
                </SearchManager>
            );
        default:
            return (
                <SearchManager>
                    <PixelTracking entryPoint={entryPoint} />
                    <FlightsManagerMobile isHotelOptionEnabled={isHotelOptionEnabled} rootDataAttr={rootDataAttr} />
                </SearchManager>
            );
    }
};

SearchManagerMobile.propTypes = {
    isHotelOptionEnabled: PropTypes.bool,
    entryPoint: EntryPointProp,
    rootDataAttr: PropTypes.object,
};

SearchManagerMobile.defaultProps = {
    isHotelOptionEnabled: false,
    entryPoint: EntryPointPropDefault,
    rootDataAttr: {},
};
